import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin, Route as RouteIcon, ArrowRight } from "lucide-react";
import { InnerPage } from "@/components/InnerPage";
import { SectionHeader } from "@/components/SectionHeader";
import { UsCoverageMap } from "@/components/UsCoverageMap";
import { pageHead } from "@/lib/seo";
import { breadcrumbsFor } from "@/lib/schema";
import { SITE } from "@/lib/site";

const PATH = "/coverage";

const META_DESCRIPTION =
  "NJ-based carrier serving the lower 48 [confirm] with long-haul and regional freight lanes. Produce, dry van, and general freight from Fox Trucking.";

export const Route = createFileRoute("/coverage")({
  head: () =>
    pageHead({
      title: "Coast-to-Coast Trucking from New Jersey | Fox Trucking",
      description: META_DESCRIPTION,
      path: PATH,
      ogTitle: "Coast-to-Coast Trucking from New Jersey | Fox Trucking",
      ogDescription: META_DESCRIPTION,
    }),
  component: CoveragePage,
});

const PRIMARY_LANES = [
  // TODO: Replace each entry with confirmed primary lanes from Fox Trucking.
  "[PRIMARY LANES — e.g. NJ to FL]",
  "[PRIMARY LANES — e.g. NJ to CA]",
  "[PRIMARY LANES — e.g. NJ to TX]",
  "[PRIMARY LANES — e.g. Northeast regional]",
  "[PRIMARY LANES — e.g. Midwest produce corridors]",
  "[PRIMARY LANES — confirm additional lanes]",
] as const;

const REGIONS = [
  {
    name: "Northeast",
    copy: `Our New Jersey home base puts us in the heart of Northeast shipping — short regional moves and long-haul originations from the Mid-Atlantic corridor.`,
  },
  {
    name: "Southeast",
    copy: `Produce and general freight flowing into and out of the Southeast — temperature-controlled or dry van, booked with dispatch that stays on the line.`,
  },
  {
    name: "Midwest",
    copy: `Cross-country freight through the Midwest industrial and distribution belt, connecting East Coast origins with central hubs.`,
  },
  {
    name: "Texas / South Central",
    copy: `Long-haul lanes into Texas and the South Central states for shippers who need reliable FTL capacity, reefer or dry.`,
  },
  {
    name: "West Coast",
    copy: `Coast-to-coast runs that finish on the West Coast — scheduled for the distance, supported by 24/7 dispatch from New Jersey.`,
  },
] as const;

const COVERAGE_FAQS = [
  {
    question: "Do you really run coast to coast?",
    answer: `Yes. ${SITE.shortName} hauls long-haul freight from our New Jersey base across the contiguous United States. [48 states / confirm] — ask dispatch about your specific origin and destination.`,
  },
  {
    question: "Do you offer regional freight as well as long haul?",
    answer:
      "Yes. We support Northeast regional work out of New Jersey in addition to coast-to-coast lanes. Share your pickup and delivery cities for a quote.",
  },
  {
    question: "What are typical transit times?",
    // TODO: Confirm published transit-time ranges by lane length / region.
    answer:
      "[TRANSIT TIMES — confirm typical days by lane with operations]. Call dispatch at " +
      SITE.phoneDisplay +
      " with your cities and we’ll give you a realistic window.",
  },
  {
    question: "Can you haul both reefer and dry van on these lanes?",
    answer:
      "Yes. Temperature-controlled produce and perishables, plus dry van general freight — depending on equipment availability for your dates.",
  },
];

function CoveragePage() {
  return (
    <InnerPage
      hero={{
        eyebrow: `Based in ${SITE.locationShort} · Since ${SITE.foundingYear}`,
        title: "Where We Haul",
        intro: `From a New Jersey home base to shippers nationwide — long-haul and regional freight with dispatch that picks up. [48 states / confirm].`,
        primaryCta: { label: "Book a Load", href: "/contact" },
        secondaryCta: { label: "Call Dispatch", href: `tel:${SITE.phoneTel}` },
        showPhoneCta: false,
      }}
      breadcrumbs={breadcrumbsFor("Coverage", PATH)}
      faqs={COVERAGE_FAQS}
      faqTitle="Coverage questions."
      faqSubtitle="Where we run, how far we go, and how to get a transit window."
      bottomCta={{
        title: "Ready to book a lane?",
        subtitle:
          "Give us the lane and freight type — we’ll confirm capacity and a realistic window.",
        bookLabel: "Book a Load",
        bookHref: "/contact",
      }}
    >
      {/* HOME BASE */}
      <section className="bg-background py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 lg:grid-cols-2 lg:px-8">
          <div>
            <SectionHeader
              eyebrow="Home base"
              title="Rooted in New Jersey. Reach that runs nationwide."
              subtitle={`${SITE.name} is based in New Jersey and serves shippers across the Northeast and nationwide — produce, dry goods, and general freight on long-haul and regional lanes.`}
            />
            <ul className="mt-8 space-y-4">
              <li className="flex gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground">
                  <MapPin className="h-6 w-6" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-display text-lg text-foreground">New Jersey headquarters</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Local relationships in the Mid-Atlantic with the capacity to keep freight moving
                    far beyond state lines.
                  </p>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-accent text-accent-foreground">
                  <RouteIcon className="h-6 w-6" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-display text-lg text-foreground">Northeast + nationwide</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Regional Northeast work and coast-to-coast hauls — one carrier, one dispatch
                    team that answers 24/7.
                  </p>
                </div>
              </li>
            </ul>
          </div>
          <div className="rounded-lg border-4 border-accent bg-secondary p-8 text-secondary-foreground">
            <p className="font-display text-xs tracking-[0.3em] text-accent">
              Since {SITE.foundingYear}
            </p>
            <p className="mt-4 font-display text-3xl leading-tight md:text-4xl">
              Call dispatch from anywhere you ship.
            </p>
            <a
              href={`tel:${SITE.phoneTel}`}
              className="mt-6 inline-flex h-14 items-center justify-center rounded-md bg-primary px-6 font-display tracking-wider text-primary-foreground hover:bg-primary/90"
            >
              {SITE.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* COVERAGE MAP */}
      <section className="bg-muted py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="Map"
            title="Coverage at a glance."
            subtitle="Default view highlights the contiguous United States. We’ll tighten this map once confirmed states and regions are locked in."
          />
          <div className="mt-12">
            <UsCoverageMap />
          </div>
        </div>
      </section>

      {/* COMMON LANES */}
      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="Lanes"
            title="Common lanes."
            subtitle="Primary corridors we run most often — placeholders until Fox Trucking confirms the published list."
          />
          <ul className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PRIMARY_LANES.map((lane) => (
              <li
                key={lane}
                className="flex items-start gap-3 rounded-lg border border-dashed border-border bg-card px-5 py-4"
              >
                <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                <span className="font-mono text-sm text-muted-foreground">{lane}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* REGIONS SERVED */}
      <section className="relative bg-secondary py-24 text-secondary-foreground">
        <div className="absolute inset-0 bg-noise opacity-60" />
        <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            tone="dark"
            eyebrow="Regions"
            title="Where shippers call us from."
            subtitle="Short takes by region — book refrigerated produce or dry van general freight on the lanes that fit your freight."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {REGIONS.map((region) => (
              <article
                key={region.name}
                className="rounded-lg border border-secondary-foreground/15 bg-secondary/50 p-6"
              >
                <h3 className="font-display text-xl text-accent">{region.name}</h3>
                <p className="mt-3 text-sm text-secondary-foreground/80">{region.copy}</p>
                <div className="mt-5 flex flex-wrap gap-3 text-sm">
                  <Link
                    to="/refrigerated-freight"
                    className="font-display tracking-wider text-accent underline-offset-2 hover:underline"
                  >
                    Reefer &amp; produce
                  </Link>
                  <span className="text-secondary-foreground/40" aria-hidden="true">
                    ·
                  </span>
                  <Link
                    to="/dry-van-freight"
                    className="font-display tracking-wider text-accent underline-offset-2 hover:underline"
                  >
                    Dry van freight
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </InnerPage>
  );
}
